import { IconCopy } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useFetchLines } from "../../api/queries";
import type { Purpose } from "../../api/types";
import { PURPOSE_LABELS } from "../../lib/metrics";
import { ErrorBlock, LoadingBlock } from "../ui/StateBlock";

const FEEDBACK_MS = 1_600;

interface FetchBlockProps {
  datasetId: string;
  versionNumber: number;
  /** What the client currently allows, so the picker offers only those. */
  permitted: Purpose[];
  /** False while the version's bytes are not there to be read. */
  enabled: boolean;
}

/**
 * The two lines a researcher copies, exactly as the server wrote them.
 *
 * The console writes this block on the side that knows the address, the id,
 * the filename and the checksum; the browser prints it **verbatim, in order,
 * and never beside a number of its own**. The third line is not decoration:
 * `sha256sum` against the number on the screen is how somebody proves the
 * bytes they hold are the bytes this console read.
 *
 * The purpose picker changes which block the server writes — it does not edit
 * one. A block the console would refuse is worse than a screen that says no,
 * so asking for a purpose the consent does not cover shows the refusal in the
 * server's own sentence instead of printing a command that would fail.
 *
 * Args:
 *     datasetId: The dataset.
 *     versionNumber: The version whose bytes these lines fetch.
 *     permitted: The purposes the current consent covers.
 *     enabled: Whether there are bytes to fetch at all.
 *
 * Returns:
 *     The Fetch block.
 */
export function FetchBlock({
  datasetId,
  versionNumber,
  permitted,
  enabled,
}: FetchBlockProps): JSX.Element {
  const [purpose, setPurpose] = useState<Purpose | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const lines = useFetchLines(datasetId, versionNumber, purpose, enabled);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(async (text: string): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), FEEDBACK_MS);
    } catch {
      // Clipboard denied or unavailable: the text stays selectable on screen.
    }
  }, []);

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Fetch</h2>
        {permitted.length > 1 ? (
          <label className="inline-field">
            <span className="inline-field__label">for</span>
            <select
              className="select select--inline"
              value={purpose ?? ""}
              onChange={(event) =>
                setPurpose((event.target.value || undefined) as Purpose | undefined)
              }
              aria-label="What the block asks for"
            >
              <option value="">the console's own choice</option>
              {permitted.map((option) => (
                <option key={option} value={option}>
                  {PURPOSE_LABELS[option].toLowerCase()}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {!enabled ? (
        <p className="state-block">
          There is nothing to fetch yet: this file is not available, and the
          console does not print a command it would refuse.
        </p>
      ) : lines.isPending ? (
        <LoadingBlock label="Asking the console for the lines…" />
      ) : lines.isError ? (
        <ErrorBlock error={lines.error} onRetry={() => void lines.refetch()} />
      ) : (
        <>
          <div className="fetch">
            <pre className="fetch__lines">
              {lines.data.lines.map((line, index) => (
                <span className="fetch__line" key={`${index}-${line}`}>
                  {line}
                  {"\n"}
                </span>
              ))}
            </pre>
            <button
              type="button"
              className="btn btn--sm fetch__copy"
              onClick={() => void copy(lines.data.lines.join("\n"))}
            >
              <IconCopy size={13} aria-hidden="true" />
              {copied ? "Copied" : "Copy"}
            </button>
            <span className="sr-only" role="status">
              {copied ? "Copied to the clipboard" : ""}
            </span>
          </div>
          <p className="form__note">
            The key goes in <span className="mono">$DK_KEY</span> — mint one on
            your Account page. The console records that a link was issued; it
            does not see the transfer, so it never claims the file moved. The
            server wrote these lines — nothing here was assembled in your
            browser.
          </p>
        </>
      )}
    </section>
  );
}
